import { useEffect, useState } from "react";
import { api, ApiError, type DietOverview, type DietMeal, type DietFoodSource } from "../../lib/api";

type DailyKey =
  | "daily_protein_g"
  | "daily_fat_g"
  | "daily_veg_cups"
  | "daily_carbs_rest_g"
  | "daily_carbs_light_g"
  | "daily_carbs_moderate_g"
  | "daily_carbs_hard_g";

// meal field <-> daily field <-> rounding step
const MACROS: { meal: keyof DietMeal; daily: DailyKey; label: string; step: number }[] = [
  { meal: "protein_g", daily: "daily_protein_g", label: "Protein g", step: 1 },
  { meal: "fat_g", daily: "daily_fat_g", label: "Fat g", step: 1 },
  { meal: "veggie_cups", daily: "daily_veg_cups", label: "Veg cups", step: 0.5 },
  { meal: "carbs_rest_g", daily: "daily_carbs_rest_g", label: "Carbs · Rest", step: 1 },
  { meal: "carbs_light_g", daily: "daily_carbs_light_g", label: "Carbs · Light", step: 1 },
  { meal: "carbs_moderate_g", daily: "daily_carbs_moderate_g", label: "Carbs · Mod", step: 1 },
  { meal: "carbs_hard_g", daily: "daily_carbs_hard_g", label: "Carbs · Hard", step: 1 },
];

const CARB_KEYS: { key: keyof DietMeal; label: string }[] = [
  { key: "carbs_rest_g", label: "Rest" },
  { key: "carbs_light_g", label: "Light" },
  { key: "carbs_moderate_g", label: "Mod" },
  { key: "carbs_hard_g", label: "Hard" },
];

const roundStep = (x: number, step: number) => Math.round(x / step) * step;

/** Split a daily total across unlocked meals, keeping locked meals fixed and the
 *  sum exact (the last unlocked meal absorbs the rounding remainder). */
function splitMacro(daily: number, vals: number[], locked: boolean[], step: number): number[] {
  const lockedSum = vals.reduce((a, v, i) => a + (locked[i] ? v : 0), 0);
  const unlocked = vals.map((_, i) => i).filter((i) => !locked[i]);
  const out = [...vals];
  if (unlocked.length === 0) return out;
  const remain = Math.max(0, daily - lockedSum);
  const per = roundStep(remain / unlocked.length, step);
  unlocked.forEach((idx, k) => {
    out[idx] = k === unlocked.length - 1 ? Math.max(0, Math.round((remain - per * (unlocked.length - 1)) * 100) / 100) : per;
  });
  return out;
}

export default function DietTemplate() {
  const [ov, setOv] = useState<DietOverview | null | "error">(null);
  const [phaseId, setPhaseId] = useState<number | null>(null);
  const [meals, setMeals] = useState<DietMeal[]>([]);
  const [goal, setGoal] = useState("fat_loss");
  const [startWeight, setStartWeight] = useState("");
  const [activePhase, setActivePhase] = useState<number | null>(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [savingMeals, setSavingMeals] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // Daily targets (Option A) + per-meal ephemeral locks.
  const [daily, setDaily] = useState<Record<DailyKey, string>>({
    daily_protein_g: "",
    daily_fat_g: "",
    daily_veg_cups: "",
    daily_carbs_rest_g: "",
    daily_carbs_light_g: "",
    daily_carbs_moderate_g: "",
    daily_carbs_hard_g: "",
  });
  const [locked, setLocked] = useState<boolean[]>([]);

  function load() {
    api.diet
      .overview()
      .then((o) => {
        setOv(o);
        if (o.plan) {
          setGoal(o.plan.goal);
          setStartWeight(o.plan.start_weight_kg != null ? String(o.plan.start_weight_kg) : "");
          setActivePhase(o.plan.active_phase_id);
          setDaily({
            daily_protein_g: o.plan.daily_protein_g != null ? String(o.plan.daily_protein_g) : "",
            daily_fat_g: o.plan.daily_fat_g != null ? String(o.plan.daily_fat_g) : "",
            daily_veg_cups: o.plan.daily_veg_cups != null ? String(o.plan.daily_veg_cups) : "",
            daily_carbs_rest_g: o.plan.daily_carbs_rest_g != null ? String(o.plan.daily_carbs_rest_g) : "",
            daily_carbs_light_g: o.plan.daily_carbs_light_g != null ? String(o.plan.daily_carbs_light_g) : "",
            daily_carbs_moderate_g: o.plan.daily_carbs_moderate_g != null ? String(o.plan.daily_carbs_moderate_g) : "",
            daily_carbs_hard_g: o.plan.daily_carbs_hard_g != null ? String(o.plan.daily_carbs_hard_g) : "",
          });
        }
        const first = o.plan?.active_phase_id ?? o.phases[0]?.id ?? null;
        setPhaseId(first);
        const ph = o.phases.find((p) => p.id === first);
        const ms = ph ? ph.meals.map((m) => ({ ...m })) : [];
        setMeals(ms);
        setLocked(ms.map(() => false));
      })
      .catch(() => setOv("error"));
  }
  useEffect(load, []);

  const sumMeals = (key: keyof DietMeal) =>
    meals.reduce((a, m) => a + (Number(m[key]) || 0), 0);

  /** Re-split the daily targets across the current meals (respecting locks). */
  function distribute() {
    setMeals((ms) => {
      const next = ms.map((m) => ({ ...m }));
      for (const mac of MACROS) {
        const target = Number(daily[mac.daily]);
        if (!Number.isFinite(target) || daily[mac.daily] === "") continue;
        const vals = next.map((m) => Number(m[mac.meal]) || 0);
        const split = splitMacro(target, vals, locked, mac.step);
        split.forEach((v, i) => ((next[i] as Record<string, unknown>)[mac.meal] = v));
      }
      return next;
    });
    setMsg("Distributed across meals — review and Save meals.");
  }

  /** Fill the daily-target inputs from the current meals' sums. */
  function targetsFromMeals() {
    setDaily({
      daily_protein_g: String(sumMeals("protein_g")),
      daily_fat_g: String(sumMeals("fat_g")),
      daily_veg_cups: String(sumMeals("veggie_cups")),
      daily_carbs_rest_g: String(sumMeals("carbs_rest_g")),
      daily_carbs_light_g: String(sumMeals("carbs_light_g")),
      daily_carbs_moderate_g: String(sumMeals("carbs_moderate_g")),
      daily_carbs_hard_g: String(sumMeals("carbs_hard_g")),
    });
  }

  const o = ov && ov !== "error" ? ov : null;

  function selectPhase(id: number) {
    setPhaseId(id);
    const ph = o?.phases.find((p) => p.id === id);
    const ms = ph ? ph.meals.map((m) => ({ ...m })) : [];
    setMeals(ms);
    setLocked(ms.map(() => false));
    setMsg(null);
  }

  function setMeal(i: number, field: keyof DietMeal, value: string | number | boolean) {
    setMeals((ms) => ms.map((m, j) => (j === i ? { ...m, [field]: value } : m)));
  }

  function addMeal() {
    setMeals((ms) => [
      ...ms,
      {
        meal_index: ms.length + 1,
        name: `Meal ${ms.length + 1}`,
        timing: "",
        protein_g: 0,
        fat_g: 0,
        veggie_cups: 0,
        carbs_rest_g: 0,
        carbs_light_g: 0,
        carbs_moderate_g: 0,
        carbs_hard_g: 0,
        is_shake: 0,
      },
    ]);
    setLocked((l) => [...l, false]);
  }

  function removeMeal(i: number) {
    setMeals((ms) => ms.filter((_, j) => j !== i));
    setLocked((l) => l.filter((_, j) => j !== i));
  }

  function toggleLock(i: number) {
    setLocked((l) => l.map((v, j) => (j === i ? !v : v)));
  }

  const dailyNum = (k: DailyKey): number | null =>
    daily[k] === "" ? null : Number(daily[k]);

  async function savePlan() {
    setSavingPlan(true);
    setMsg(null);
    try {
      await api.diet.savePlan({
        goal,
        start_weight_kg: startWeight ? Number(startWeight) : null,
        active_phase_id: activePhase,
        meals_per_day: meals.length || o?.plan?.meals_per_day || 6,
        daily_protein_g: dailyNum("daily_protein_g"),
        daily_fat_g: dailyNum("daily_fat_g"),
        daily_veg_cups: dailyNum("daily_veg_cups"),
        daily_carbs_rest_g: dailyNum("daily_carbs_rest_g"),
        daily_carbs_light_g: dailyNum("daily_carbs_light_g"),
        daily_carbs_moderate_g: dailyNum("daily_carbs_moderate_g"),
        daily_carbs_hard_g: dailyNum("daily_carbs_hard_g"),
      });
      setMsg("Plan saved.");
      load();
    } catch (e) {
      setMsg(e instanceof ApiError ? `API ${e.status}` : (e as Error).message);
    } finally {
      setSavingPlan(false);
    }
  }

  async function saveMeals() {
    if (phaseId == null) return;
    setSavingMeals(true);
    setMsg(null);
    try {
      await api.diet.savePhaseMeals(
        phaseId,
        meals.map((m, i) => ({ ...m, meal_index: i + 1 })),
      );
      setMsg("Meals saved.");
      load();
    } catch (e) {
      setMsg(e instanceof ApiError ? `API ${e.status}` : (e as Error).message);
    } finally {
      setSavingMeals(false);
    }
  }

  if (ov === "error") return <p className="muted">Couldn't load your diet template.</p>;
  if (!o) return <p className="muted">Loading…</p>;

  const numInput = (v: number, on: (n: number) => void) => (
    <input
      inputMode="decimal"
      value={v === 0 ? "" : String(v)}
      onChange={(e) => on(e.target.value === "" ? 0 : Number(e.target.value))}
      className="input px-1.5 py-1 text-center text-sm"
    />
  );

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h2 className="section-title mb-1">Your template</h2>
        <p className="text-ink-subtle text-small">
          Seeded from your RP PDF — review and correct it here. Macros are grams of the nutrient
          (protein / carb / fat) plus veggies in cups; carbs change by training-day type.
        </p>
      </div>

      {/* Plan settings */}
      <div className="card card-pad space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="field-label">Goal</span>
            <select value={goal} onChange={(e) => setGoal(e.target.value)} className="input mt-1.5">
              <option value="fat_loss">Fat loss</option>
              <option value="muscle_gain">Muscle gain</option>
            </select>
          </label>
          <label className="block">
            <span className="field-label">Start weight (kg)</span>
            <input inputMode="decimal" value={startWeight} onChange={(e) => setStartWeight(e.target.value)} className="input mt-1.5" />
          </label>
        </div>
        <label className="block">
          <span className="field-label">Active phase</span>
          <select
            value={activePhase ?? ""}
            onChange={(e) => setActivePhase(Number(e.target.value))}
            className="input mt-1.5"
          >
            {o.phases.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </label>
        <button className="btn-accent w-full py-2.5" onClick={savePlan} disabled={savingPlan}>
          {savingPlan ? "Saving…" : "Save plan"}
        </button>
      </div>

      {/* Daily targets (Option A) */}
      <div className="card card-pad space-y-3">
        <div className="flex items-center justify-between">
          <span className="field-label">Daily targets</span>
          <button type="button" className="text-xs text-accent" onClick={targetsFromMeals}>
            Set from current meals
          </button>
        </div>
        <p className="text-xs text-ink-subtle">
          Your whole-day amounts. Hit <b>Distribute</b> below to split them across however many meals
          you keep — change the meal count freely and the day total stays the same.
        </p>
        <div className="grid grid-cols-3 gap-2">
          <DailyField label="Protein g">{numInput(Number(daily.daily_protein_g) || 0, (n) => setDaily((d) => ({ ...d, daily_protein_g: n ? String(n) : "" })))}</DailyField>
          <DailyField label="Fat g">{numInput(Number(daily.daily_fat_g) || 0, (n) => setDaily((d) => ({ ...d, daily_fat_g: n ? String(n) : "" })))}</DailyField>
          <DailyField label="Veg cups">{numInput(Number(daily.daily_veg_cups) || 0, (n) => setDaily((d) => ({ ...d, daily_veg_cups: n ? String(n) : "" })))}</DailyField>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-wider text-ink-subtle">Carbs (g) by day type</span>
          <div className="mt-1 grid grid-cols-4 gap-2">
            <DailyField label="Rest">{numInput(Number(daily.daily_carbs_rest_g) || 0, (n) => setDaily((d) => ({ ...d, daily_carbs_rest_g: n ? String(n) : "" })))}</DailyField>
            <DailyField label="Light">{numInput(Number(daily.daily_carbs_light_g) || 0, (n) => setDaily((d) => ({ ...d, daily_carbs_light_g: n ? String(n) : "" })))}</DailyField>
            <DailyField label="Mod">{numInput(Number(daily.daily_carbs_moderate_g) || 0, (n) => setDaily((d) => ({ ...d, daily_carbs_moderate_g: n ? String(n) : "" })))}</DailyField>
            <DailyField label="Hard">{numInput(Number(daily.daily_carbs_hard_g) || 0, (n) => setDaily((d) => ({ ...d, daily_carbs_hard_g: n ? String(n) : "" })))}</DailyField>
          </div>
        </div>
      </div>

      {/* Phase meal editor */}
      <div>
        <div className="mb-3 flex flex-wrap gap-1.5">
          {o.phases.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => selectPhase(p.id)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition duration-fast ${
                phaseId === p.id ? "bg-accent/10 text-accent" : "text-ink-muted hover:text-ink"
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>

        {/* Day-total readout vs target + distribute */}
        <div className="card-pad mb-3 bg-canvas">
          <div className="flex items-center justify-between">
            <span className="field-label">Day total ({meals.length} meals)</span>
            <button type="button" className="btn-accent px-3 py-1.5 text-xs" onClick={distribute}>
              Distribute across {meals.length} meals
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs tabular-nums">
            <DayTotal label="P" sum={sumMeals("protein_g")} target={dailyNum("daily_protein_g")} />
            <DayTotal label="F" sum={sumMeals("fat_g")} target={dailyNum("daily_fat_g")} />
            <DayTotal label="Veg" sum={sumMeals("veggie_cups")} target={dailyNum("daily_veg_cups")} />
            <DayTotal label="C·rest" sum={sumMeals("carbs_rest_g")} target={dailyNum("daily_carbs_rest_g")} />
            <DayTotal label="C·hard" sum={sumMeals("carbs_hard_g")} target={dailyNum("daily_carbs_hard_g")} />
          </div>
        </div>

        <div className="space-y-3">
          {meals.map((m, i) => (
            <div key={i} className={`card card-pad ${locked[i] ? "border-accent/40" : ""}`}>
              <div className="mb-2 flex items-center gap-2">
                <span className="font-display text-sm text-ink-subtle">{i + 1}</span>
                <input
                  value={m.name ?? ""}
                  onChange={(e) => setMeal(i, "name", e.target.value)}
                  className="input flex-1 py-1 text-sm"
                  placeholder="Meal name"
                />
                <button
                  type="button"
                  onClick={() => toggleLock(i)}
                  title="Lock keeps this meal fixed when distributing"
                  className={`rounded px-1.5 py-1 text-xs ${locked[i] ? "text-accent" : "text-ink-subtle hover:text-ink"}`}
                >
                  {locked[i] ? "🔒 locked" : "🔓 lock"}
                </button>
                <label className="flex items-center gap-1 text-xs text-ink-subtle">
                  <input type="checkbox" checked={!!m.is_shake} onChange={(e) => setMeal(i, "is_shake", e.target.checked ? 1 : 0)} />
                  shake
                </label>
              </div>
              <input
                value={m.timing ?? ""}
                onChange={(e) => setMeal(i, "timing", e.target.value)}
                className="input mb-2 w-full py-1 text-xs"
                placeholder="Timing (e.g. Bedtime)"
              />
              <div className="grid grid-cols-3 gap-2">
                <Field label="Protein g">{numInput(m.protein_g, (n) => setMeal(i, "protein_g", n))}</Field>
                <Field label="Fat g">{numInput(m.fat_g, (n) => setMeal(i, "fat_g", n))}</Field>
                <Field label="Veg cups">{numInput(m.veggie_cups, (n) => setMeal(i, "veggie_cups", n))}</Field>
              </div>
              <div className="mt-2">
                <span className="field-label">Carbs (g) by day type</span>
                <div className="mt-1 grid grid-cols-4 gap-2">
                  {CARB_KEYS.map((ck) => (
                    <Field key={ck.key} label={ck.label}>
                      {numInput(m[ck.key] as number, (n) => setMeal(i, ck.key, n))}
                    </Field>
                  ))}
                </div>
              </div>
              <button type="button" className="mt-2 text-xs text-loss" onClick={() => removeMeal(i)}>
                Remove meal
              </button>
            </div>
          ))}
        </div>

        <div className="mt-3 flex gap-2">
          <button type="button" className="btn-secondary flex-1 py-2.5" onClick={addMeal}>
            + Add meal
          </button>
          <button className="btn-accent flex-1 py-2.5" onClick={saveMeals} disabled={savingMeals}>
            {savingMeals ? "Saving…" : "Save meals"}
          </button>
        </div>
      </div>

      {msg && <p className="text-sm text-ink-muted">{msg}</p>}

      {/* Food source reference */}
      <details className="card-pad">
        <summary className="field-label cursor-pointer">Food sources & servings</summary>
        <div className="mt-3 space-y-3">
          {(["protein", "carb", "fat", "veg", "workout_carb"] as const).map((cat) => (
            <FoodCat key={cat} cat={cat} items={o.food_sources.filter((f) => f.category === cat)} />
          ))}
        </div>
      </details>
    </div>
  );
}

const CAT_LABEL: Record<string, string> = {
  protein: "Protein",
  carb: "Healthy carbs",
  fat: "Healthy fats",
  veg: "Vegetables",
  workout_carb: "Workout carbs",
};

function FoodCat({ cat, items }: { cat: string; items: DietFoodSource[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">{CAT_LABEL[cat]}</div>
      <div className="mt-1 text-sm text-ink-muted">
        {items.map((it) => it.name).join(" · ")}
        {items[0]?.serving_note && <div className="text-xs text-ink-subtle">serving ≈ {items[0].serving_note}</div>}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

const DailyField = Field;

/** Day-total chip: sum vs target, green when they match (±1). */
function DayTotal({ label, sum, target }: { label: string; sum: number; target: number | null }) {
  const rounded = Math.round(sum * 10) / 10;
  const ok = target != null && Math.abs(sum - target) <= 1;
  const off = target != null && !ok;
  return (
    <span className={off ? "text-loss" : ok ? "text-income" : "text-ink-muted"}>
      {label} {rounded}
      {target != null ? `/${target}` : ""}
    </span>
  );
}
